import React from 'react';
import { LOGO_URL } from '../data/mock';
import { Facebook, Twitter, Linkedin, Instagram, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AREAS_BY_REGION } from '../data/areas';
import { ExpertBadge } from './ExpertBadge';

// Legal entity information - centralized for consistency
export const LEGAL_ENTITY = {
  name: 'Trustee for Keystone Heritage Trust',
  abn: '83 164 418 663',
  website: 'CompareYourOptions.com.au',
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <img
              src={LOGO_URL}
              alt="CompareYourOptions.com.au"
              className="h-10 w-auto mb-4 brightness-0 invert"
            />
            <p className="text-sm text-gray-400 mb-4">
              Your free, independent comparison platform for smart home energy solutions.
            </p>
            <div className="mb-5">
              <ExpertBadge variant="dark" align="left" />
            </div>
            <div className="flex gap-3">
              <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-cyo-orange rounded-full flex items-center justify-center transition-colors" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-cyo-orange rounded-full flex items-center justify-center transition-colors" aria-label="Twitter">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-cyo-orange rounded-full flex items-center justify-center transition-colors" aria-label="LinkedIn">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="#" className="w-8 h-8 bg-gray-800 hover:bg-cyo-orange rounded-full flex items-center justify-center transition-colors" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-white font-semibold mb-4">Products</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/compare" className="hover:text-cyo-orange transition-colors">Solar Systems</Link></li>
              <li><Link to="/compare" className="hover:text-cyo-orange transition-colors">Battery Storage</Link></li>
              <li><Link to="/compare" className="hover:text-cyo-orange transition-colors">EV Chargers</Link></li>
              <li><Link to="/compare" className="hover:text-cyo-orange transition-colors">Heat Pumps</Link></li>
              <li><Link to="/compare" className="hover:text-cyo-orange transition-colors">Heating & Cooling</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-cyo-orange transition-colors">About Us</Link></li>
              <li><Link to="/blog" className="hover:text-cyo-orange transition-colors">Blog</Link></li>
              <li><a href="/#how-it-works" className="hover:text-cyo-orange transition-colors">How It Works</a></li>
              <li><Link to="/compare" className="hover:text-cyo-orange transition-colors">Compare Now</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-cyo-orange" />
                <a href="mailto:hello@compareyouroptions.com.au" className="hover:text-cyo-orange transition-colors">
                  hello@compareyouroptions.com.au
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-cyo-orange mt-0.5" />
                <span>Australia</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Areas We Service */}
        <div className="mt-10 pt-8 border-t border-gray-800">
          <h4 className="text-white font-semibold mb-5">Areas We Service</h4>
          <div className="space-y-6">
            {AREAS_BY_REGION.map(({ region, areas }) => (
              <div key={region} data-testid={`footer-region-${region.toLowerCase().replace(/\s+/g, '-')}`}>
                <p className="text-xs font-bold uppercase tracking-wider text-cyo-orange mb-2">
                  {region}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-2 text-sm">
                  {areas.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/${a.slug}`}
                      data-testid={`footer-area-${a.slug}`}
                      className="text-gray-400 hover:text-cyo-orange transition-colors"
                    >
                      Solar, Battery &amp; Energy Solutions in {a.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Ownership Statement */}
        <div className="mt-10 pt-8 border-t border-gray-800">
          <p className="text-xs text-gray-500 text-center">
            {LEGAL_ENTITY.website} is owned and operated by {LEGAL_ENTITY.name} (ABN {LEGAL_ENTITY.abn}).
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
            <p>© {currentYear} CompareYourOptions.com.au. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-cyo-orange transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-cyo-orange transition-colors">Terms & Conditions</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
